import React, { useState, useEffect } from 'react';
import { TabsContainer } from '../../components/shared/TabsContainer';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import {
    ToolsContainer,
    ConversionTable,
    GradientInput,
    InputLabel,
    InputLabelWide,
    ResultSpan,
    TabContent,
    InputWrapper,
    InputGroup,
    DensitySideBySide,
} from './ToolsPage.components';
import { FlowConverter, LengthConverter, TemperatureConverter, VolumeConverter } from '../../components/tools';
import { normalizeNumericInput } from '../../components/tools/conversionUtils';

interface ToolsPageProps {}

const ToolsPage: React.FC<ToolsPageProps> = () => {
    const [activeTab, setActiveTab] = useState('density');

    // Pipe capacity state (default values from old system)
    const [pipeDiameter, setPipeDiameter] = useState('6');
    const [pipeLength, setPipeLength] = useState('10');

    // Density conversion state (default values from old system)
    const [densityCorrectionFactor, setDensityCorrectionFactor] = useState('0.0009');
    const [densityApiGravity, setDensityApiGravity] = useState('');
    const [densityTempF, setDensityTempF] = useState('59');
    const [densityTempC, setDensityTempC] = useState('15');
    const [densityLitreWeight, setDensityLitreWeight] = useState('1');
    const [densityPoundPerGallon, setDensityPoundPerGallon] = useState('0');
    // Second set of density values (default values from old system)
    const [densityTemp2F, setDensityTemp2F] = useState('77');
    const [densityTemp2C, setDensityTemp2C] = useState('25');
    const [densityLitreWeight2, setDensityLitreWeight2] = useState('');
    const [densityPoundPerGallon2, setDensityPoundPerGallon2] = useState('');

    // Pressure conversion state (default value of 1 bar)
    const [pressureBar, setPressureBar] = useState('1');
    const [pressureKgCm2, setPressureKgCm2] = useState('');
    const [pressurePascal, setPressurePascal] = useState('');
    const [pressureKiloPascal, setPressureKiloPascal] = useState('');
    const [pressureMegaPascal, setPressureMegaPascal] = useState('');
    const [pressureMmHg, setPressureMmHg] = useState('');
    const [pressurePsi, setPressurePsi] = useState('');

    // Viscosity conversion state (default values from old system)
    const [viscosityDensity, setViscosityDensity] = useState('1');
    const [viscosityDynamic, setViscosityDynamic] = useState('100');
    const [viscosityKinematic, setViscosityKinematic] = useState('');

    // Conversion factors from CommodityBookWebsite
    const pressureConversionFactors = {
        bar: 1,
        kgCm2: 1.019716213,
        pascal: 100000,
        kiloPascal: 100,
        megaPascal: 0.1,
        mmHg: 750.0616827,
        psi: 14.503773773,
    };

    // Helper function to remove trailing zeros
    const formatNumber = (value: number, precision: number = 3): string => {
        return parseFloat(value.toFixed(precision)).toString();
    };

    // Calculate API Gravity from Litre Weight (matching old system logic)
    const calculateApiGravityFromLitreWeight = (litreWeight: number, tempC: number, correctionFactor: number): string => {
        // Convert to 60°F (15.5555556°C) reference temperature
        const litreWeight60F = litreWeight + (tempC - 15.5555556) * correctionFactor;
        if (litreWeight60F) {
            const apiGravity = 141.5 / litreWeight60F - 131.5;
            return formatNumber(apiGravity, 4);
        }
        return '';
    };

    // Initialize API Gravity and other calculated values on mount
    useEffect(() => {
        const litreWeight = parseFloat(densityLitreWeight) || 0;
        const tempC = parseFloat(densityTempC) || 15;
        const correctionFactor = parseFloat(densityCorrectionFactor) || 0;

        if (litreWeight > 0 && (tempC || tempC === 0) && (correctionFactor || correctionFactor === 0)) {
            // Calculate and set API Gravity
            const apiGravity = calculateApiGravityFromLitreWeight(litreWeight, tempC, correctionFactor);
            setDensityApiGravity(apiGravity);

            // Calculate pound per gallon
            setDensityPoundPerGallon(formatNumber((litreWeight * 3.785412) / 0.45359237, 3));

            // Calculate second temperature values
            const temp2C = parseFloat(densityTemp2C) || 25;
            const litreWeight2Value = litreWeight + (tempC - temp2C) * correctionFactor;
            setDensityLitreWeight2(formatNumber(litreWeight2Value, 4));
            setDensityPoundPerGallon2(formatNumber((litreWeight2Value * 3.785412) / 0.45359237, 4));
        }

        // Initialize pressure conversions from default 1 bar
        const barValue = parseFloat(pressureBar) || 0;
        if (barValue > 0) {
            setPressureKgCm2(formatNumber(barValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(barValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(barValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(barValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(barValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(barValue * pressureConversionFactors.psi, 6));
        }

        // Initialize viscosity conversions from default 100 mPa.s
        const dynamicValue = parseFloat(viscosityDynamic) || 0;
        if (dynamicValue > 0) {
            const density = parseFloat(viscosityDensity) || 1;
            setViscosityKinematic(formatNumber(dynamicValue / density, 3));
        }
    }, []); // Run only on mount

    // Flow conversion handlers
    // Pipe capacity calculations
    const calculatePipeCapacities = () => {
        const diameter = parseFloat(pipeDiameter) || 0;
        const length = parseFloat(pipeLength) || 0;

        // Convert diameter from inches to meters
        const diameterMeters = diameter * 0.0254;

        // Calculate volume in cubic meters: π * r² * length
        const volumeM3 = Math.PI * Math.pow(diameterMeters / 2, 2) * length;

        // Match old system: convert cubic meters to liters first, then to gallons
        const volumeLiters = volumeM3 * 1000;
        const volumeGallons = volumeLiters / 3.785412;

        return {
            liter: formatNumber(volumeLiters, 6),
            cubicMeter: formatNumber(volumeM3, 6),
            gallon: formatNumber(volumeGallons, 6),
            cubicFeet: formatNumber(volumeGallons / 7.48052, 6),
            barrel: formatNumber(volumeGallons / 42, 6),
        };
    };

    const pipeCapacities = calculatePipeCapacities();

    // Pressure conversion handlers
    const handlePressureBarChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressureBar(value);
        if (value === '') {
            setPressureKgCm2('');
            setPressurePascal('');
            setPressureKiloPascal('');
            setPressureMegaPascal('');
            setPressureMmHg('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setPressureKgCm2(formatNumber(numValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(numValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(numValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(numValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(numValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(numValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressureKgCm2Change = (value: string) => {
        value = normalizeNumericInput(value);
        setPressureKgCm2(value);
        if (value === '') {
            setPressureBar('');
            setPressurePascal('');
            setPressureKiloPascal('');
            setPressureMegaPascal('');
            setPressureMmHg('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.kgCm2;
            setPressureBar(formatNumber(baseValue, 6));
            setPressurePascal(formatNumber(baseValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(baseValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(baseValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(baseValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(baseValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressurePascalChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressurePascal(value);
        if (value === '') {
            setPressureBar('');
            setPressureKgCm2('');
            setPressureKiloPascal('');
            setPressureMegaPascal('');
            setPressureMmHg('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.pascal;
            setPressureBar(formatNumber(baseValue, 6));
            setPressureKgCm2(formatNumber(baseValue * pressureConversionFactors.kgCm2, 6));
            setPressureKiloPascal(formatNumber(baseValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(baseValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(baseValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(baseValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressureKiloPascalChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressureKiloPascal(value);
        if (value === '') {
            setPressureBar('');
            setPressureKgCm2('');
            setPressurePascal('');
            setPressureMegaPascal('');
            setPressureMmHg('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.kiloPascal;
            setPressureBar(formatNumber(baseValue, 6));
            setPressureKgCm2(formatNumber(baseValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(baseValue * pressureConversionFactors.pascal, 6));
            setPressureMegaPascal(formatNumber(baseValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(baseValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(baseValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressureMegaPascalChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressureMegaPascal(value);
        if (value === '') {
            setPressureBar('');
            setPressureKgCm2('');
            setPressurePascal('');
            setPressureKiloPascal('');
            setPressureMmHg('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.megaPascal;
            setPressureBar(formatNumber(baseValue, 6));
            setPressureKgCm2(formatNumber(baseValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(baseValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(baseValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMmHg(formatNumber(baseValue * pressureConversionFactors.mmHg, 6));
            setPressurePsi(formatNumber(baseValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressureMmHgChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressureMmHg(value);
        if (value === '') {
            setPressureBar('');
            setPressureKgCm2('');
            setPressurePascal('');
            setPressureKiloPascal('');
            setPressureMegaPascal('');
            setPressurePsi('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.mmHg;
            setPressureBar(formatNumber(baseValue, 6));
            setPressureKgCm2(formatNumber(baseValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(baseValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(baseValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(baseValue * pressureConversionFactors.megaPascal, 6));
            setPressurePsi(formatNumber(baseValue * pressureConversionFactors.psi, 6));
        }
    };

    const handlePressurePsiChange = (value: string) => {
        value = normalizeNumericInput(value);
        setPressurePsi(value);
        if (value === '') {
            setPressureBar('');
            setPressureKgCm2('');
            setPressurePascal('');
            setPressureKiloPascal('');
            setPressureMegaPascal('');
            setPressureMmHg('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / pressureConversionFactors.psi;
            setPressureBar(formatNumber(baseValue, 6));
            setPressureKgCm2(formatNumber(baseValue * pressureConversionFactors.kgCm2, 6));
            setPressurePascal(formatNumber(baseValue * pressureConversionFactors.pascal, 6));
            setPressureKiloPascal(formatNumber(baseValue * pressureConversionFactors.kiloPascal, 6));
            setPressureMegaPascal(formatNumber(baseValue * pressureConversionFactors.megaPascal, 6));
            setPressureMmHg(formatNumber(baseValue * pressureConversionFactors.mmHg, 6));
        }
    };

    // Temperature conversion handlers
    // Viscosity conversion handlers (simplified implementation)
    const handleViscosityDynamicChange = (value: string) => {
        value = normalizeNumericInput(value);
        setViscosityDynamic(value);
        if (value === '') {
            setViscosityKinematic('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            // Kinematic viscosity = dynamic viscosity / density
            const density = parseFloat(viscosityDensity) || 1;
            setViscosityKinematic(formatNumber(numValue / density, 3));
        }
    };

    const handleViscosityKinematicChange = (value: string) => {
        value = normalizeNumericInput(value);
        setViscosityKinematic(value);
        if (value === '') {
            setViscosityDynamic('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            // Dynamic viscosity = kinematic viscosity * density
            const density = parseFloat(viscosityDensity) || 1;
            const dynamicValue = numValue * density;
            setViscosityDynamic(formatNumber(dynamicValue, 3));
        }
    };

    const handleViscosityDensityChange = (value: string) => {
        value = normalizeNumericInput(value);
        setViscosityDensity(value);
        if (value === '') {
            return;
        }
        const density = parseFloat(value);
        if (!isNaN(density) && density !== 0) {
            // Recalculate kinematic viscosity from current dynamic viscosity
            const dynamicValue = parseFloat(viscosityDynamic);
            if (!isNaN(dynamicValue)) {
                setViscosityKinematic(formatNumber(dynamicValue / density, 3));
            }
        }
    };

    // Volume conversion handlers
    // Density conversion handlers
    const handleApiGravityChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityApiGravity(value);
        if (value === '') {
            setDensityLitreWeight('');
            setDensityPoundPerGallon('');
            setDensityLitreWeight2('');
            setDensityPoundPerGallon2('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            // Convert API gravity to density at 60°F (15.5555556°C)
            const litreWeight60F = 141.5 / (numValue + 131.5);
            const correctionFactor = parseFloat(densityCorrectionFactor) || 0;
            const temp1C = parseFloat(densityTempC) || 15;
            // Convert from 60°F back to the reference temperature (matching old system)
            const litreWeight = litreWeight60F + (15.5555556 - temp1C) * correctionFactor;
            setDensityLitreWeight(formatNumber(litreWeight, 4));
            // Use same conversion formulas as old system
            setDensityPoundPerGallon(formatNumber((litreWeight * 3.785412) / 0.45359237, 4));

            // Update second temperature section
            const temp2C = parseFloat(densityTemp2C) || 15;
            const correctedWeight = litreWeight + (temp1C - temp2C) * correctionFactor;
            setDensityLitreWeight2(formatNumber(correctedWeight, 4));
            setDensityPoundPerGallon2(formatNumber((correctedWeight * 3.785412) / 0.45359237, 4));
        }
    };

    const handleDensityTempFChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityTempF(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const celsius = ((numValue - 32) * 5) / 9;
                setDensityTempC(formatNumber(celsius, 2));
                updateLitreWeight2();
            }
        } else {
            setDensityTempC('');
        }
    };

    const handleDensityTempCChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityTempC(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const fahrenheit = (numValue * 9) / 5 + 32;
                setDensityTempF(formatNumber(fahrenheit, 2));
                updateLitreWeight2();
            }
        } else {
            setDensityTempF('');
        }
    };

    const handleDensityTemp2FChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityTemp2F(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const celsius = ((numValue - 32) * 5) / 9;
                setDensityTemp2C(formatNumber(celsius, 2));
                updateLitreWeight2();
            }
        } else {
            setDensityTemp2C('');
        }
    };

    const handleDensityTemp2CChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityTemp2C(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const fahrenheit = (numValue * 9) / 5 + 32;
                setDensityTemp2F(formatNumber(fahrenheit, 2));
                updateLitreWeight2();
            }
        } else {
            setDensityTemp2F('');
        }
    };

    const updateLitreWeight2 = (correctionFactorOverride?: number) => {
        const correctionFactor = correctionFactorOverride !== undefined ? correctionFactorOverride : parseFloat(densityCorrectionFactor) || 0;
        const temp1C = parseFloat(densityTempC) || 15;
        const temp2C = parseFloat(densityTemp2C) || 15;
        const litreWeight = parseFloat(densityLitreWeight) || 0;

        if (litreWeight > 0) {
            // Match old system formula: litreWeight + ((temp1C - temp2C) * correctionFactor)
            const correctedWeight = litreWeight + (temp1C - temp2C) * correctionFactor;
            setDensityLitreWeight2(formatNumber(correctedWeight, 4));
            // Use same conversion formulas as old system
            setDensityPoundPerGallon2(formatNumber((correctedWeight * 3.785412) / 0.45359237, 4));

            // Also update first section's poundPerGallon (matching old system)
            setDensityPoundPerGallon(formatNumber((litreWeight * 3.785412) / 0.45359237, 3));
        }
    };

    const handleLitreWeightChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityLitreWeight(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                const tempC = parseFloat(densityTempC) || 15;
                const correctionFactor = parseFloat(densityCorrectionFactor) || 0;

                // Recalculate API Gravity
                const apiGravity = calculateApiGravityFromLitreWeight(numValue, tempC, correctionFactor);
                setDensityApiGravity(apiGravity);

                // Use same conversion formulas as old system
                setDensityPoundPerGallon(formatNumber((numValue * 3.785412) / 0.45359237, 4));
                updateLitreWeight2();
            }
        } else {
            setDensityApiGravity('');
            setDensityPoundPerGallon('');
        }
    };

    const handlePoundPerGallonChange = (value: string) => {
        value = normalizeNumericInput(value);
        setDensityPoundPerGallon(value);
        if (value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                // Reverse formula: litreWeight = poundPerGallon * 0.45359237 / 3.785412
                const litreWeight = (numValue * 0.45359237) / 3.785412;
                setDensityLitreWeight(formatNumber(litreWeight, 4));

                // Recalculate API Gravity from the new litre weight
                const tempC = parseFloat(densityTempC) || 15;
                const correctionFactor = parseFloat(densityCorrectionFactor) || 0;
                const apiGravity = calculateApiGravityFromLitreWeight(litreWeight, tempC, correctionFactor);
                setDensityApiGravity(apiGravity);

                updateLitreWeight2();
            }
        } else {
            setDensityLitreWeight('');
            setDensityApiGravity('');
        }
    };

    return (
        <PageWrapper>
            <PageContainer>
                <ToolsContainer>
                    <TabsContainer>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'density' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('density');
                                }}
                            >
                                Density
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'flow' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('flow');
                                }}
                            >
                                Flow
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'length' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('length');
                                }}
                            >
                                Length
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'pipe' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('pipe');
                                }}
                            >
                                Pipe Capacity
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'pressure' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('pressure');
                                }}
                            >
                                Pressure
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'temperature' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('temperature');
                                }}
                            >
                                Temperature
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'viscosity' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('viscosity');
                                }}
                            >
                                Viscosity
                            </a>
                        </li>
                        <li>
                            <a
                                className={`tab-btn ${activeTab === 'volume' ? 'active' : ''}`}
                                href="#tabs-volume"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('volume');
                                }}
                            >
                                Volume
                            </a>
                        </li>
                    </TabsContainer>

                    {/* Density Tab */}
                    {activeTab === 'density' && (
                        <TabContent>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Parameter</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Correction Factor:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="litreWeightCorrectionFactor"
                                                        type="text"
                                                        value={densityCorrectionFactor}
                                                        onChange={(e) => {
                                                            const newValue = normalizeNumericInput(e.target.value);
                                                            setDensityCorrectionFactor(newValue);

                                                            // Recalculate API Gravity with new correction factor
                                                            const litreWeight = parseFloat(densityLitreWeight) || 0;
                                                            const tempC = parseFloat(densityTempC) || 15;
                                                            const correctionFactor = parseFloat(newValue) || 0;
                                                            if (litreWeight > 0) {
                                                                const apiGravity = calculateApiGravityFromLitreWeight(
                                                                    litreWeight,
                                                                    tempC,
                                                                    correctionFactor
                                                                );
                                                                setDensityApiGravity(apiGravity);
                                                            }

                                                            // Pass the new correction factor value directly
                                                            updateLitreWeight2(correctionFactor);
                                                        }}
                                                    />
                                                    <InputLabel htmlFor="litreWeightCorrectionFactor">kg/°C</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>API Gravity at 60 °F:</th>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="APIgravity"
                                                        type="text"
                                                        value={densityApiGravity}
                                                        onChange={(e) => handleApiGravityChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="APIgravity">°API</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody></tbody>
                            </ConversionTable>
                            <DensitySideBySide>
                                <ConversionTable>
                                    <thead>
                                        <tr>
                                            <th>Density from:</th>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <GradientInput
                                                            id="litreWeightDisplayedTemperatureF"
                                                            type="text"
                                                            value={densityTempF}
                                                            onChange={(e) => handleDensityTempFChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="litreWeightDisplayedTemperatureF">°F</InputLabel>
                                                        <GradientInput
                                                            id="litreWeightDisplayedTemperatureC"
                                                            type="text"
                                                            value={densityTempC}
                                                            onChange={(e) => handleDensityTempCChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="litreWeightDisplayedTemperatureC">°C</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Litre weight:</td>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <GradientInput
                                                            id="litreWeight"
                                                            type="text"
                                                            value={densityLitreWeight}
                                                            onChange={(e) => handleLitreWeightChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="litreWeight">kg/l</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Pound/Gallon (US)</td>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <GradientInput
                                                            id="poundPerGallon"
                                                            type="text"
                                                            value={densityPoundPerGallon}
                                                            onChange={(e) => handlePoundPerGallonChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="poundpergallon">lb/gal</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                    </tbody>
                                </ConversionTable>
                                <ConversionTable>
                                    <thead>
                                        <tr>
                                            <th>Density to:</th>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <GradientInput
                                                            id="litreWeightDisplayedTemperature2F"
                                                            type="text"
                                                            value={densityTemp2F}
                                                            onChange={(e) => handleDensityTemp2FChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="litreWeightDisplayedTemperature2F">°F</InputLabel>
                                                        <GradientInput
                                                            id="litreWeightDisplayedTemperature2C"
                                                            type="text"
                                                            value={densityTemp2C}
                                                            onChange={(e) => handleDensityTemp2CChange(e.target.value)}
                                                        />
                                                        <InputLabel htmlFor="litreWeightDisplayedTemperature2C">°C</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Litre weight:</td>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <ResultSpan>{densityLitreWeight2}</ResultSpan>
                                                        <InputLabel htmlFor="litreWeight2">kg/l</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Pound/Gallon (US)</td>
                                            <td>
                                                <InputWrapper>
                                                    <InputGroup>
                                                        <ResultSpan>{densityPoundPerGallon2}</ResultSpan>
                                                        <InputLabel htmlFor="poundpergallon">lb/gal</InputLabel>
                                                    </InputGroup>
                                                </InputWrapper>
                                            </td>
                                        </tr>
                                    </tbody>
                                </ConversionTable>
                            </DensitySideBySide>
                        </TabContent>
                    )}

                    {/* Flow Tab */}
                    {activeTab === 'flow' && <FlowConverter />}

                    {/* Length Tab */}
                    {activeTab === 'length' && <LengthConverter />}

                    {/* Pipe Capacity Tab */}
                    {activeTab === 'pipe' && (
                        <TabContent>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Diameter In Inches:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="pipediameter"
                                                        type="text"
                                                        value={pipeDiameter}
                                                        onChange={(e) => setPipeDiameter(normalizeNumericInput(e.target.value))}
                                                    />
                                                    <InputLabel htmlFor="pipediameter">&quot;</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Length In Meters:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="pipelength"
                                                        type="text"
                                                        value={pipeLength}
                                                        onChange={(e) => setPipeLength(normalizeNumericInput(e.target.value))}
                                                    />
                                                    <InputLabel htmlFor="pipelength">m</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Pipe Capacity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Litre:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <ResultSpan id="pipelitre">{pipeCapacities.liter}</ResultSpan>
                                                    <InputLabel htmlFor="pipelitre">l</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Cubic Meter:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <ResultSpan id="pipecubicmeter">{pipeCapacities.cubicMeter}</ResultSpan>
                                                    <InputLabel htmlFor="pipecubicmeter">m³</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Gallon (US):</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <ResultSpan id="pipegallon">{pipeCapacities.gallon}</ResultSpan>
                                                    <InputLabel htmlFor="pipegallon">gal</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Cubic Feet:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <ResultSpan id="pipecubicfeet">{pipeCapacities.cubicFeet}</ResultSpan>
                                                    <InputLabel htmlFor="pipecubicfeet">ft³</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Barrel:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <ResultSpan id="pipebarrel">{pipeCapacities.barrel}</ResultSpan>
                                                    <InputLabel htmlFor="pipebarrel">bbl</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                        </TabContent>
                    )}

                    {/* Pressure Tab */}
                    {activeTab === 'pressure' && (
                        <TabContent>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Pressure</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Bar:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="bar"
                                                        type="text"
                                                        value={pressureBar}
                                                        onChange={(e) => handlePressureBarChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="bar">bar</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Kilogram-force/Square Centimeter:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="kgpercm"
                                                        type="text"
                                                        value={pressureKgCm2}
                                                        onChange={(e) => handlePressureKgCm2Change(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="kgpercm">kg/cm²</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Pascal:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="pascal"
                                                        type="text"
                                                        value={pressurePascal}
                                                        onChange={(e) => handlePressurePascalChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="pascal">Pa</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Kilopascal:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="kilopascal"
                                                        type="text"
                                                        value={pressureKiloPascal}
                                                        onChange={(e) => handlePressureKiloPascalChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="kilopascal">kPa</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Megapascal:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="megapascal"
                                                        type="text"
                                                        value={pressureMegaPascal}
                                                        onChange={(e) => handlePressureMegaPascalChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="megapascal">MPa</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Millimeter of mercury (0°C):</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="mmHg"
                                                        type="text"
                                                        value={pressureMmHg}
                                                        onChange={(e) => handlePressureMmHgChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="mmHg">mmHg</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Pounds/Square Inch:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="psi"
                                                        type="text"
                                                        value={pressurePsi}
                                                        onChange={(e) => handlePressurePsiChange(e.target.value)}
                                                    />
                                                    <InputLabel htmlFor="psi">psi</InputLabel>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                        </TabContent>
                    )}

                    {/* Temperature Tab */}
                    {activeTab === 'temperature' && <TemperatureConverter />}

                    {/* Viscosity Tab */}
                    {activeTab === 'viscosity' && (
                        <TabContent>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Parameter</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Density:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="density"
                                                        type="text"
                                                        value={viscosityDensity}
                                                        onChange={(e) => handleViscosityDensityChange(e.target.value)}
                                                    />
                                                    <InputLabelWide htmlFor="density">kg/l</InputLabelWide>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Dynamic Viscosity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Millipascal-second:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="dynamicviscosity"
                                                        type="text"
                                                        value={viscosityDynamic}
                                                        onChange={(e) => handleViscosityDynamicChange(e.target.value)}
                                                    />
                                                    <InputLabelWide htmlFor="dynamicviscosity">mPa.s (or cP)</InputLabelWide>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                            <ConversionTable>
                                <thead>
                                    <tr>
                                        <th>Kinematic Viscosity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Centistokes:</td>
                                        <td>
                                            <InputWrapper>
                                                <InputGroup>
                                                    <GradientInput
                                                        id="kinematicviscosity"
                                                        type="text"
                                                        value={viscosityKinematic}
                                                        onChange={(e) => handleViscosityKinematicChange(e.target.value)}
                                                    />
                                                    <InputLabelWide htmlFor="kinematicviscosity">cSt (or mm²/s)</InputLabelWide>
                                                </InputGroup>
                                            </InputWrapper>
                                        </td>
                                    </tr>
                                </tbody>
                            </ConversionTable>
                        </TabContent>
                    )}

                    {/* Volume Tab */}
                    {activeTab === 'volume' && <VolumeConverter />}
                </ToolsContainer>
            </PageContainer>
        </PageWrapper>
    );
};

export default ToolsPage;
